import {HomeScreens, ShopperScreens} from '../../../../home/navigation/types';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  RecommendedItemCell,
  ReplacementItemDetails,
  SubstitutionItemParams,
  SubstitutionRequestParams,
} from '../../../../../src/itemDetails/types/itemReplacement';
import {getClientType, getStoreName} from '@buncha/common/types/common';
import {isWeb, useCheckIfB2BStore} from '@buncha/utils/common';
import {
  useSendSubstitutionRequestToBuyer,
  useSubstituteItemInRun,
} from '../../../../../src/itemDetails/hooks/itemReplacement';

import {Analytics} from '@buncha/config/analytics/analytics';
import AppModal from '../../../../common/components/CustomModal/AppModal';
import {EventType} from '@buncha/config/analytics/type';
import {H5Text} from '@buncha/components/core/text';
import {HBox} from '@buncha/components/core/view';
import If from '@buncha/components/conditional/If';
import {ItemDetailsScreen} from '../../../../itemDetails/navigation/types';
import {Modules} from '@buncha/utils/types';
import QRCode from '../../../../../src/components/core/icon/QRCode';
import ReplacementModalBody from '../replacementModalBody';
import {SubstitutionMode} from '../../../../../src/itemDetails/types/itemDetails';
import {navigation} from '../../../../../src/config/navigation';
import {styles} from './styles';
import {useCheckItemToAdd} from '@buncha/batchDetails/hooks/ScannedAddItem';
import {useColorThemeType} from '../../../../../src/appStyles/hooks/theme';
import {useGetLocale} from '@buncha/localization/hooks';
import {useIsMeijerStore} from '@buncha/home/hooks/home';
import {useRoute} from '@react-navigation/native';

interface RequestReplacementModalProps {
  open: boolean;
  onClose: () => void;
  catalogItemId: number;
  itemSource: string;
  indexPosition: number;
  catalogItem?: RecommendedItemCell;
  orderItem?: ReplacementItemDetails | undefined;
  isB2BStore?: boolean;
}

const RequestReplacementModal: React.FC<
  RequestReplacementModalProps
> = props => {
  const route = useRoute();
  const routeParams: {runId?: number; substitutionItemId?: number} | undefined =
    route?.params;
  const runId = routeParams?.runId || 0;
  const theme = useColorThemeType('main');
  const [alreadyApproved, setAlreadyApproved] = useState(
    props?.isB2BStore || false,
  );
  const {sendSubstitutionRequestToBuyer, loading} =
    useSendSubstitutionRequestToBuyer();
  const {substituteItemInRun, itemSubstituteLoading, errorMessage} =
    useSubstituteItemInRun();
  const {title, body, button} =
    useGetLocale(Modules.itemDetails, ShopperScreens.ItemDetails) ?? {};
  const {addItemCheckError, checkItem} = useCheckItemToAdd();

  const [showErrorModal, setSetshowErrorModal] = useState('');

  const handleOnClose = () => {
    setSetshowErrorModal('');
    props?.onClose();
  };

  useEffect(() => {
    if (errorMessage || addItemCheckError)
      setSetshowErrorModal(errorMessage || addItemCheckError);
  }, [addItemCheckError, errorMessage]);

  const catalogItemId = props?.catalogItemId || 0;
  const item = props?.orderItem;
  const orderItemId = item?.id || 0;
  const orderId = item?.order?.id;
  const quantity =
    item?.shopperItemChangesForShopper?.updatedQuantity || item?.quantity || 0;
  const catalogItem = props.catalogItem;
  const isWebMode = isWeb();

  const storeIndex = item?.order?.run?.store?.elasticSearchIndex;
  const store = item?.order?.run.store;
  const storeId = store?.id ?? 0;
  const {checkIfB2BStore} = useCheckIfB2BStore();
  const isMeijerStore = useIsMeijerStore(storeId);

  const paramsToSend: SubstitutionRequestParams = {
    catalogItemId: catalogItemId,
    orderItemId: orderItemId,
    quantity: quantity,
    itemSource: props?.itemSource,
    indexPosition: props?.indexPosition,
  };

  const getText = useMemo(() => {
    if (alreadyApproved)
      return {
        header: title?.customerApprovalRequired,
        body: body?.customerMustConfimation,
        topCTA: `${isWebMode ? button?.pickItem : button?.foundItem}`,
        bottomCTA: button?.cancel,
      };
    return {
      header: title?.areYouSure,
      body: body?.addItem,
      topCTA: button?.requestReplacement,
      bottomCTA: button?.approved,
    };
  }, [
    alreadyApproved,
    body?.addItem,
    body?.customerMustConfimation,
    button?.approved,
    button?.cancel,
    button?.foundItem,
    button?.pickItem,
    button?.requestReplacement,
    isWebMode,
    title?.areYouSure,
    title?.customerApprovalRequired,
  ]);

  const topCTAHandler = useCallback(async () => {
    const isB2BStore = checkIfB2BStore(storeId);
    if (!alreadyApproved && catalogItemId && orderItemId && quantity) {
      await sendSubstitutionRequestToBuyer(paramsToSend);
      props?.onClose();
      navigation.navigate(HomeScreens.Shopper, {
        screen: ShopperScreens.BatchDetailsPage,
        params: {
          runId: runId,
        },
      });
    } else if (alreadyApproved) {
      if (
        !catalogItem ||
        !catalogItemId ||
        !orderId ||
        !quantity ||
        !orderItemId ||
        !storeIndex
      )
        return;
      const substituteParams: SubstitutionItemParams = {
        catalogItemId: catalogItemId,
        isCustomItem: false,
        itemImageUrl: catalogItem.itemImageUrl,
        name: catalogItem.name,
        orderId: orderId,
        position: 0,
        orderItemId: orderItemId,
        price: catalogItem.price,
        quantity: quantity, // change to dynamic if required
        substitutedBy: JSON.stringify(item),
        substitutionRequestParmas: {
          substitutionMode: SubstitutionMode.approvedOverCallBeforeRequestSend,
        },
        indexPosition: catalogItem.indexPosition,
        itemSource: catalogItem.itemSource,
        isPriceSensitive: Boolean(catalogItem.flags?.priceSensitive),
        isWeightSensitive: Boolean(catalogItem.flags?.weightSensitive),
        metaLoggingForKpi: !isB2BStore
          ? catalogItem?.metaLoggingForKpi
          : undefined,
        substitutionItemId: routeParams?.substitutionItemId,
      };
      if (isMeijerStore) {
        const res = await checkItem(
          catalogItem?.upc,
          orderId,
          storeIndex,
          runId,
          catalogItem?.metaLoggingForKpi,
        );
        if (res) {
          props.onClose();
          navigation.navigate(ShopperScreens.ScannedAddItemPage, {
            storeIndex: storeIndex,
            runId: runId,
            orderId: orderId,
            storeId: storeId,
            plu: catalogItem?.upc,
            substituteParams: substituteParams,
            returnPage: ShopperScreens.BatchDetailsPage,
            quantity,
          });
        }
        return;
      }
      const response = await substituteItemInRun(substituteParams);
      if (response) {
        props.onClose();
        if (isWebMode)
          navigation.navigate(HomeScreens.Shopper, {
            screen: ShopperScreens.BatchDetailsPage,
            params: {runId},
          });
        else
          navigation.navigate(ShopperScreens.ItemDetails, {
            screen: ItemDetailsScreen.ItemScanner,
            params: {
              orderItemId,
              storeIndex,
              runId,
              quantity,
              source: 'Replacement Item Modal',
            },
          });
      }
    } else {
      props?.onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alreadyApproved, catalogItemId, orderItemId, props, quantity]);

  const bottomCTAHandler = useCallback(() => {
    Analytics.eventWithProps('Customer Approval Request', {
      type: EventType.cta,
      client: getClientType(isMeijerStore),
      storeType: getStoreName(String(storeId)),
      originalItem: item,
      substituteItem: catalogItem,
      runId: runId,
      store: store,
    });
    if (!alreadyApproved) setAlreadyApproved(true);
    else props?.onClose();
  }, [
    alreadyApproved,
    catalogItem,
    isMeijerStore,
    item,
    props,
    runId,
    store,
    storeId,
  ]);

  return (
    <React.Fragment>
      <If condition={!showErrorModal}>
        <AppModal
          isOpen={props?.open}
          onClose={() => props?.onClose()}
          hideTopCta={!alreadyApproved}
          topCTAHandelr={topCTAHandler}
          bottomCTAHandelr={bottomCTAHandler}
          title={getText?.header}
          modalBody={
            <ReplacementModalBody
              text={getText?.body}
              item={item}
              catalogItem={catalogItem}
            />
          }
          ctaBottomTitle={getText?.bottomCTA}
          ctaTopTitle={getText?.topCTA}
          topCTAColor={theme?.background.green[100]}
          ctaTopLeftComponent={
            alreadyApproved &&
            !isWebMode && <QRCode viewBox="-3 -1 25 15" style={styles.icon} />
          }
          loading={loading || itemSubstituteLoading}
          storeId={storeId}
        />
      </If>
      <If condition={Boolean(showErrorModal)}>
        <AppModal
          isOpen={true}
          onClose={handleOnClose}
          topCTAHandelr={handleOnClose}
          title={title?.info}
          hideBottomCta
          modalBody={
            <HBox style={styles.modalBody}>
              <H5Text>{showErrorModal}</H5Text>
            </HBox>
          }
          ctaTopTitle={button?.ok}
          topCTAColor={theme?.background.green[100]}
          storeId={storeId}
        />
      </If>
    </React.Fragment>
  );
};

export default React.memo(RequestReplacementModal);
